import type { AssignExtractTemplateBody } from '~/api/extract';

/** React Query key for the extract admin state; mutations invalidate it to refresh the state view. */
export const EXTRACT_ADMIN_STATE_QUERY_KEY = ['extract-admin-state'] as const;

export type ExtractToolTabKey = 'sync' | 'automation' | 'template' | 'assign' | 'state';

export interface ExtractToolTab {
  key: ExtractToolTabKey;
  label: string;
  testid: string;
}

/** The top-level sub-tabs of the Extract Tools page, in display order. */
export const EXTRACT_TOOL_TABS: ExtractToolTab[] = [
  { key: 'sync', label: 'Sync Projections', testid: 'extract-tab-sync' },
  { key: 'automation', label: 'Create Automation', testid: 'extract-tab-automation' },
  { key: 'template', label: 'Create Template', testid: 'extract-tab-template' },
  { key: 'assign', label: 'Assign Template', testid: 'extract-tab-assign' },
  { key: 'state', label: 'Current State', testid: 'extract-tab-state' },
];

export interface AssignTemplateFormState {
  extractTemplateId: string;
  organizationId: string | null;
  allOrgs: boolean;
}

/**
 * Build the request body for assigning a template. When `allOrgs` is set the
 * organization is omitted (the server fans out to every active org); otherwise the
 * single selected `organizationId` is sent.
 */
export function buildAssignTemplateBody(state: AssignTemplateFormState): AssignExtractTemplateBody {
  if (state.allOrgs) {
    return { extractTemplateId: state.extractTemplateId, allOrgs: true };
  }
  return { extractTemplateId: state.extractTemplateId, organizationId: state.organizationId ?? undefined };
}

/** Whether the assign form has enough input to submit (a template, and either allOrgs or an org). */
export function canSubmitAssignTemplate(state: AssignTemplateFormState): boolean {
  if (!state.extractTemplateId) return false;
  return state.allOrgs || !!state.organizationId;
}

/**
 * Human-readable label for which organizations a template is available to.
 * - `'None'` when it has no active assignments.
 * - `'All organizations'` when it's assigned to every org (assignment count covers `organizationCount`).
 * - Otherwise the comma-separated org names.
 */
export function templateAvailabilityLabel(assignedOrgNames: string[], organizationCount: number): string {
  if (assignedOrgNames.length === 0) return 'None';
  if (organizationCount > 0 && assignedOrgNames.length >= organizationCount) return 'All organizations';
  return [...assignedOrgNames].sort((a, b) => a.localeCompare(b)).join(', ');
}

export function createAutomationConfirmMessage(newName: string, activeName: string | undefined): string {
  return activeName
    ? `Archive ${activeName} and activate ${newName}?`
    : `Activate ${newName}? Any active automation will be archived.`;
}
